import { useEffect, useRef, useState } from 'react'
import { useForm } from 'react-hook-form'

import { zodResolver } from '@hookform/resolvers/zod'

import { apiPostFetcher } from '@/common/api/fetcher'
import { useWidgetPageListApi } from '@/common/api/modules/widgetPage'
import { GenerateCssResponse } from '@/common/api/params/widget'
import {
  BUNDLE_MAX_ITEMS_CHOICES,
  BUNDLE_MAX_ITEMS_MAX,
  BUNDLE_MAX_ITEMS_MIN,
  widgetLayoutTypeName
} from '@/common/app/modules/recommend/widget'
import { widgetPageListPath } from '@/common/app/path'
import { useSiteId } from '@/common/app/site'
import { registerNumberOption } from '@/common/form/register'
import { SitePreviewResponse } from '@/common/model/recommend/analysis'
import { WidgetLayoutType } from '@/common/model/recommend/widget'

import Badge, { BadgeType } from '@/components/view/atoms/badge/Badge'
import AiButton from '@/components/view/atoms/button/AiButton'
import Button, { ButtonColor } from '@/components/view/atoms/button/Button'
import GroupButton from '@/components/view/atoms/button/GroupButton'
import IconButton from '@/components/view/atoms/button/IconButton'
import TextButton from '@/components/view/atoms/button/TextButton'
import FormCaption from '@/components/view/atoms/form/FormCaption'
import FormLabel from '@/components/view/atoms/form/FormLabel'
import { IconColor, IconType } from '@/components/view/atoms/icon/Icon'
import IconText from '@/components/view/atoms/icon/IconText'
import Loading from '@/components/view/atoms/icon/Loading'
import { SelectorSize } from '@/components/view/atoms/selector/Selector'
import Text, { TextColor } from '@/components/view/atoms/text/Text'
import TextArea from '@/components/view/atoms/textarea/Textarea'
import { TextBoxSize } from '@/components/view/atoms/textfield/TextField'
import AreaContent from '@/components/view/layouts/area/AreaContent'
import GuidanceAreaContent from '@/components/view/layouts/area/GuidanceAreaContent'
import FormRow from '@/components/view/layouts/form/FormRow'
import ModalContent, { ModalContentType } from '@/components/view/layouts/modal/ModalContent'
import ModalFooter from '@/components/view/molecules/modal/ModalFooter'

import {
  VisualCssSchemaType,
  convertVisualCssToApplyVars,
  visualCssSchema
} from '@/components/container/form/modules/recommend/visualCss/visualCssSchema'
import FormCodeTextArea from '@/components/container/form/widgets/FormCodeTextArea'
import FormError from '@/components/container/form/widgets/FormError'
import FormSelector from '@/components/container/form/widgets/FormSelector'

const W2SR_CSS = `
  .w2sr-widget { font-family: -apple-system, BlinkMacSystemFont, sans-serif; margin: 16px 0; }
  .w2sr-widget-title { font-size: 18px; font-weight: 700; color: #1e293b; margin: 0 0 16px; padding-bottom: 8px; border-bottom: 2px solid #2563eb; display: inline-block; }
  .w2sr-layout-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(140px, 1fr)); gap: 12px; }
  .w2sr-layout-carousel { display: flex; gap: 12px; overflow-x: auto; scroll-snap-type: x mandatory; padding-bottom: 8px; }
  .w2sr-layout-carousel .w2sr-item { min-width: 140px; scroll-snap-align: start; }
  .w2sr-layout-list { display: flex; flex-direction: column; gap: 8px; }
  .w2sr-layout-list .w2sr-item { flex-direction: row; }
  .w2sr-layout-list .w2sr-item-img { width: 80px; height: 80px; }
  .w2sr-item { display: flex; flex-direction: column; text-decoration: none; color: inherit; background: #fff; border-radius: 8px; overflow: hidden; box-shadow: 0 1px 3px rgba(0,0,0,0.08); }
  .w2sr-item-img { width: 100%; aspect-ratio: 1; object-fit: cover; background: #e2e8f0; }
  .w2sr-item-info { padding: 8px; }
  .w2sr-item-name { font-size: 13px; font-weight: 500; color: #1e293b; margin: 0 0 4px; line-height: 1.3; }
  .w2sr-item-price { font-size: 14px; font-weight: 700; color: #dc2626; margin: 0; }
  /* バンドル（まとめてカートに入れる）の既定表示。配信JSの既定スタイルと合わせる */
  .w2sr-layout-bundle { container-type: inline-size; color: #1e1e1e; }
  .w2sr-layout-bundle .w2sr-widget-title { display: block; text-align: center; border-bottom: none; padding-bottom: 0; margin: 0 0 20px; }
  .w2sr-bundle { display: grid; grid-template-columns: auto minmax(0, 1fr); gap: 20px 32px; align-items: center; }
  .w2sr-bundle-visual { display: flex; align-items: center; gap: 10px; }
  .w2sr-bundle-thumb { position: relative; display: block; width: clamp(72px, 15cqw, 150px); aspect-ratio: 1; background: #fff; border: 1px solid #ececec; }
  .w2sr-bundle-img { display: flex; align-items: center; justify-content: center; width: 100%; height: 100%; object-fit: contain; color: #94a3b8; font-size: 11px; background: #f1f5f9; }
  .w2sr-bundle-check { position: absolute; top: 6px; left: 6px; width: 18px; height: 18px; margin: 0; accent-color: #111; }
  .w2sr-bundle-thumb.is-off .w2sr-bundle-img { opacity: 0.35; }
  .w2sr-bundle-plus { font-size: 26px; font-weight: 700; line-height: 1; color: #111; }
  .w2sr-bundle-summary { display: flex; flex-direction: column; gap: 10px; align-items: flex-start; }
  .w2sr-bundle-total { margin: 0; font-size: 14px; }
  .w2sr-bundle-total-price { font-size: 17px; font-weight: 700; margin: 0 2px; }
  .w2sr-bundle-cart { font-size: 14px; letter-spacing: 0.06em; color: #fff; background: #111; border: 0; padding: 14px 44px; cursor: pointer; }
  .w2sr-bundle-note { margin: 0; font-size: 11px; color: #777; }
  .w2sr-bundle-list { grid-column: 1 / -1; list-style: none; margin: 0; padding: 0; display: flex; flex-direction: column; gap: 12px; }
  .w2sr-bundle-item { display: flex; flex-wrap: wrap; align-items: center; gap: 4px 12px; font-size: 14px; }
  .w2sr-bundle-item.is-off { opacity: 0.45; }
  .w2sr-bundle-label, .w2sr-bundle-name { font-weight: 700; color: inherit; }
  .w2sr-bundle-variation { font-size: 13px; background: transparent; border: 0; border-bottom: 1px solid #9a9a9a; padding: 4px 22px 4px 4px; }
  @container (max-width: 600px) {
    .w2sr-bundle { grid-template-columns: minmax(0, 1fr); }
    .w2sr-bundle-visual { order: 1; }
    .w2sr-bundle-thumb { flex: 1; width: auto; }
    .w2sr-bundle-list { order: 2; }
    .w2sr-bundle-summary { order: 3; align-items: stretch; }
    .w2sr-bundle-cart { width: 100%; }
  }
`

const SAMPLE_ITEMS = [
  { name: 'サンプル商品 A', price: 2980 },
  { name: 'サンプル商品 B', price: 1580 },
  { name: 'サンプル商品 C', price: 3200 },
  { name: 'サンプル商品 D', price: 4500 },
  { name: 'サンプル商品 E', price: 1980 },
  { name: 'サンプル商品 F', price: 3680 }
]

const ALL_PROMPTS = [
  'サイトの配色に合わせてレコメンドパーツの色を調整してください',
  '角丸を増やしてモダンなデザインにしてください',
  '商品カードに影を追加して浮き上がるようにしてください',
  'フォントサイズを大きくして読みやすくしてください',
  '商品名と価格のスペースを広げてください',
  'レコメンドパーツ全体に枠線を追加してセクションを区切ってください',
  '背景色をサイトに馴染む色に変更してください',
  '商品画像を丸くトリミングしてください',
  '価格を目立つ色に変更してください',
  'タイトルのフォントをサイトに合わせてください',
  'カード間の余白を広げてゆったりしたレイアウトにしてください',
  'ホバー時にカードが少し浮き上がるアニメーションを追加してください',
  '商品名を2行で切り詰めて統一感を出してください',
  'レコメンドパーツの横幅を画面いっぱいに広げてください',
  'グリッドを3列固定にしてください'
]

function pickRandomPrompts(all: string[], count: number): string[] {
  return [...all].sort(() => Math.random() - 0.5).slice(0, count)
}

// バンドルのプレビュー用バリエーション（最後の1つは在庫なしの例）
const SAMPLE_VARIATIONS = ['カラー A', 'カラー B', 'カラー C']

// バンドルのプレビュー用マークアップ。主商品＋併売候補（1〜3件）。クラス名は配信JSの描画と合わせる
function buildBundleMarkup(titleTemplate: string, maxItems: number): string {
  const count = Math.min(
    Math.max(maxItems || BUNDLE_MAX_ITEMS_MAX, BUNDLE_MAX_ITEMS_MIN),
    BUNDLE_MAX_ITEMS_MAX
  )
  const items = SAMPLE_ITEMS.slice(0, count + 1)
  const total = items.reduce((sum, item) => sum + item.price, 0)
  const variationSelect = `<select class="w2sr-bundle-variation">${SAMPLE_VARIATIONS.map((v, i) =>
    i === SAMPLE_VARIATIONS.length - 1 ? `<option disabled>${v}（在庫なし）</option>` : `<option>${v}</option>`
  ).join('')}</select>`
  const visual = items
    .map(
      (_, i) =>
        `${i > 0 ? '<span class="w2sr-bundle-plus" aria-hidden="true">+</span>' : ''}<label class="w2sr-bundle-thumb${i === 0 ? ' w2sr-bundle-main' : ''}"><input type="checkbox" class="w2sr-bundle-check" checked><span class="w2sr-bundle-img">No Image</span></label>`
    )
    .join('')
  const rows = items
    .map((item, i) => {
      const name =
        i === 0
          ? `<span class="w2sr-bundle-label">この商品：</span><span class="w2sr-bundle-name">${item.name}</span>`
          : `<a class="w2sr-bundle-name" href="#">${item.name}</a>`
      return `<li class="w2sr-bundle-item${i === 0 ? ' w2sr-bundle-main' : ''}">${name}<span class="w2sr-bundle-price">&yen;${item.price.toLocaleString()}</span>${variationSelect}</li>`
    })
    .join('')
  const title = titleTemplate ? `<h3 class="w2sr-widget-title">${titleTemplate}</h3>` : ''
  return `<div class="w2sr-widget w2sr-layout-bundle">${title}<div class="w2sr-bundle"><div class="w2sr-bundle-visual">${visual}</div><div class="w2sr-bundle-summary"><p class="w2sr-bundle-total">合計金額<span class="w2sr-bundle-total-price">&yen;${total.toLocaleString()}</span> ・ <span class="w2sr-bundle-count">${items.length}点</span></p><button type="button" class="w2sr-bundle-cart">まとめてカートに入れる</button><p class="w2sr-bundle-note">最終的な金額はカートでご確認ください</p></div><ul class="w2sr-bundle-list">${rows}</ul></div></div>`
}

function buildWidgetMarkup(layoutType: string, titleTemplate: string, maxItems: number): string {
  if (layoutType === WidgetLayoutType.BUNDLE) {
    return buildBundleMarkup(titleTemplate, maxItems)
  }
  const layoutClass = 'w2sr-layout-' + (layoutType || 'grid')
  const items = SAMPLE_ITEMS.slice(0, maxItems || 4)
  return `<div class="w2sr-widget">${titleTemplate ? `<h3 class="w2sr-widget-title">${titleTemplate}</h3>` : ''}<div class="w2sr-items ${layoutClass}">${items.map((item) => `<div class="w2sr-item"><div class="w2sr-item-img" style="display:flex;align-items:center;justify-content:center;color:#94a3b8;font-size:11px">No Image</div><div class="w2sr-item-info"><p class="w2sr-item-name">${item.name}</p><p class="w2sr-item-price">&yen;${item.price.toLocaleString()}</p></div></div>`).join('')}</div></div>`
}

function buildIframeScript(widgetHtml: string, w2srCss: string, customCss: string): string {
  const escHtml = JSON.stringify(widgetHtml)
  const escBaseCss = JSON.stringify(w2srCss)
  const escCustomCss = JSON.stringify(customCss)
  return `
<style id="w2sr-editor-style">
  ._w2sr-hover-highlight { outline: 3px dashed #6366f1 !important; outline-offset: 2px; cursor: pointer !important; }
  ._w2sr-insert-marker { outline: 3px solid #6366f1 !important; outline-offset: 2px; }
  ._w2sr-select-banner {
    position: fixed; top: 0; left: 0; right: 0; z-index: 99999;
    background: linear-gradient(135deg, #6366f1, #8b5cf6); color: #fff;
    text-align: center; padding: 10px 16px; font-size: 14px; font-weight: 600;
    font-family: -apple-system, BlinkMacSystemFont, sans-serif;
    box-shadow: 0 4px 20px rgba(99,102,241,0.4);
  }
  #_w2sr-widget-container { transition: outline 0.2s; }
</style>
<script>
(function(){
  var placed = false;
  var banner = document.createElement('div');
  banner.className = '_w2sr-select-banner';
  banner.textContent = 'レコメンドパーツの挿入位置をクリックしてください（要素の下に挿入されます）';
  document.body.prepend(banner);
  var lastHover = null;
  document.addEventListener('mouseover', function(e) {
    if (placed) return;
    var el = e.target;
    if (el === banner || el.closest('._w2sr-select-banner') || el.id === '_w2sr-widget-container' || el.closest('#_w2sr-widget-container')) return;
    if (lastHover && lastHover !== el) lastHover.classList.remove('_w2sr-hover-highlight');
    el.classList.add('_w2sr-hover-highlight');
    lastHover = el;
  }, true);
  document.addEventListener('mouseout', function(e) {
    if (placed) return;
    if (lastHover) lastHover.classList.remove('_w2sr-hover-highlight');
  }, true);
  document.addEventListener('click', function(e) {
    if (placed) {
      var widget = document.getElementById('_w2sr-widget-container');
      if (widget && !widget.contains(e.target)) {
        e.preventDefault(); e.stopPropagation();
        widget.remove();
        placed = false;
        banner.textContent = 'レコメンドパーツの挿入位置をクリックしてください（要素の下に挿入されます）';
        banner.style.display = '';
      }
      return;
    }
    e.preventDefault(); e.stopPropagation();
    var el = e.target;
    if (el === banner || el.closest('._w2sr-select-banner') || el.id === '_w2sr-widget-container') return;
    if (lastHover) lastHover.classList.remove('_w2sr-hover-highlight');
    var container = document.createElement('div');
    container.id = '_w2sr-widget-container';
    container.innerHTML = ${escHtml};
    var styleBase = document.createElement('style');
    styleBase.id = '_w2sr-base-css';
    styleBase.textContent = ${escBaseCss};
    document.head.appendChild(styleBase);
    var styleCustom = document.createElement('style');
    styleCustom.id = '_w2sr-custom-css';
    styleCustom.textContent = ${escCustomCss};
    document.head.appendChild(styleCustom);
    el.insertAdjacentElement('afterend', container);
    container.scrollIntoView({ behavior: 'smooth', block: 'center' });
    placed = true;
    banner.textContent = '配置完了 — 別の場所に移動するにはレコメンドパーツ外の要素をクリック';
    banner.style.background = 'linear-gradient(135deg, #10b981, #34d399)';
    window.parent.postMessage({ type: 'w2sr-placed', tag: el.tagName, className: el.className }, '*');
  }, true);
  window.addEventListener('message', function(e) {
    if (e.data && e.data.type === 'w2sr-update-css') {
      var s = document.getElementById('_w2sr-custom-css');
      if (s) s.textContent = e.data.css;
    }
    if (e.data && e.data.type === 'w2sr-update-widget') {
      var c = document.getElementById('_w2sr-widget-container');
      if (c) c.innerHTML = e.data.html;
    }
  });
})();
</script>`
}

interface Props {
  values: {
    siteId: number
    widgetPageId: number | null
    layoutType: WidgetLayoutType
    titleTemplate: string
    maxItems: number
    useTitle: boolean
    currentCss: string
  }
  hide: () => void
  onApply: (values: {
    custom_css: string
    layout_type: WidgetLayoutType
    max_items: number
    title_template: string
    use_title: boolean
    widget_page_id: number | null
  }) => void
}

const VisualCssForm = ({ values, hide, onApply }: Props) => {
  const {
    register,
    handleSubmit,
    watch,
    setValue,
    control,
    formState: { errors }
  } = useForm<VisualCssSchemaType>({
    resolver: zodResolver(visualCssSchema),
    defaultValues: {
      useTitle: values.useTitle ?? !!values.titleTemplate,
      titleTemplate: values.titleTemplate || '',
      layoutType: values.layoutType || WidgetLayoutType.GRID,
      maxItems: values.maxItems || 6,
      customCss: values.currentCss || '',
      widgetPageId: values.widgetPageId
    }
  })

  const siteId = useSiteId()

  const useTitle = watch('useTitle')
  const titleTemplate = watch('titleTemplate')
  const layoutType = watch('layoutType')
  const maxItems = watch('maxItems')
  const customCss = watch('customCss')
  const widgetPageId = watch('widgetPageId')

  const effectiveTitleTemplate = useTitle ? titleTemplate : ''

  const [siteHtml, setSiteHtml] = useState('')
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState('')
  const [instructions, setInstructions] = useState('')
  const [generating, setGenerating] = useState(false)
  const [genError, setGenError] = useState('')
  const [placed, setPlaced] = useState(false)
  const [suggestedPrompts, setSuggestedPrompts] = useState(() => pickRandomPrompts(ALL_PROMPTS, 5))
  const iframeRef = useRef<HTMLIFrameElement>(null)
  const iframeHtmlRef = useRef('')

  useEffect(() => {
    if (!widgetPageId) {
      return
    }
    let cancelled = false
    const fetchPreview = async () => {
      setLoading(true)
      setLoadError('')
      try {
        const data = await apiPostFetcher<SitePreviewResponse>(
          `/api/sites/${values.siteId}/widgets/site-preview`,
          {
            arg: {
              widget_page_id: widgetPageId
            }
          }
        )
        if (!cancelled) setSiteHtml(data.html)
      } catch (e: unknown) {
        if (!cancelled) setLoadError(e instanceof Error ? e.message : 'サイトの取得に失敗しました')
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    fetchPreview()
    return () => {
      cancelled = true
    }
  }, [values.siteId, widgetPageId])

  useEffect(() => {
    function onMsg(e: MessageEvent) {
      if (e.data?.type === 'w2sr-placed') setPlaced(true)
    }
    window.addEventListener('message', onMsg)
    return () => window.removeEventListener('message', onMsg)
  }, [])

  useEffect(() => {
    if (iframeRef.current?.contentWindow) {
      iframeRef.current.contentWindow.postMessage({ type: 'w2sr-update-css', css: customCss }, '*')
    }
  }, [customCss])

  useEffect(() => {
    if (placed && iframeRef.current?.contentWindow) {
      iframeRef.current.contentWindow.postMessage(
        {
          type: 'w2sr-update-widget',
          html: buildWidgetMarkup(layoutType, effectiveTitleTemplate, maxItems)
        },
        '*'
      )
    }
  }, [layoutType, effectiveTitleTemplate, maxItems, placed])

  if (siteHtml && !iframeHtmlRef.current) {
    const widgetMarkup = buildWidgetMarkup(layoutType, effectiveTitleTemplate, maxItems)
    iframeHtmlRef.current = siteHtml.replace(
      /<\/body>/i,
      buildIframeScript(widgetMarkup, W2SR_CSS, values.currentCss || '') + '</body>'
    )
  }

  const { data: widgetPages, isLoading: isWidgetPagesLoading } = useWidgetPageListApi({
    site_id: siteId
  })
  const widgetPageChoices = (widgetPages ?? []).map((page) => ({
    name: page.name,
    value: page.id
  }))

  const handleGenerateCSS = async () => {
    if (!instructions.trim()) return
    setGenerating(true)
    setGenError('')
    try {
      const data = await apiPostFetcher<GenerateCssResponse>(
        `/api/sites/${values.siteId}/widgets/generate-css`,
        {
          arg: {
            widget_page_id: values.widgetPageId,
            instructions
          }
        }
      )

      setValue('customCss', data.generated_css)
    } catch (e: unknown) {
      setGenError(e instanceof Error ? e.message : 'CSS生成に失敗しました')
    } finally {
      setGenerating(false)
    }
  }

  const onSubmit = (data: VisualCssSchemaType) => {
    onApply(convertVisualCssToApplyVars(data))
    hide()
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <ModalContent maxKeep type={ModalContentType.NO_FRAME}>
        <div className='flex h-full'>
          {/* Left: Preview */}
          <div className='relative flex-1 overflow-hidden px-10'>
            {!widgetPageId ? (
              <GuidanceAreaContent>
                <Text>デザイン参照ページを設定してください</Text>
              </GuidanceAreaContent>
            ) : (
              loading && (
                <GuidanceAreaContent>
                  <IconText text='ECサイトを読み込み中' iconType={IconType.LOADING} />
                </GuidanceAreaContent>
              )
            )}
            {loadError && (
              <GuidanceAreaContent>
                <Text color={TextColor.CRITICAL}>読み込みエラー: {loadError}</Text>
              </GuidanceAreaContent>
            )}
            {!loading && !loadError && !siteHtml && (
              <GuidanceAreaContent>
                <Text color={TextColor.CRITICAL}>HTMLが取得できませんでした</Text>
              </GuidanceAreaContent>
            )}
            {!loading && !loadError && siteHtml && (
              <iframe
                ref={iframeRef}
                srcDoc={iframeHtmlRef.current}
                className='h-full w-full border-none bg-white'
                title='ECサイトプレビュー'
              />
            )}
          </div>
          {/* Right: Sidebar */}
          <div className='flex w-100 shrink-0 flex-col overflow-y-auto border-l border-slate-200 bg-white'>
            <FormCodeTextArea
              control={control}
              name='customCss'
              placeholder='.w2sr-widget { ... }'
              height='200px'
              noBorder
            />
            <AreaContent>
              <FormRow direction='vertical' gap={2}>
                <FormLabel id='widgetPageId'>デザイン参照ページ</FormLabel>
                <FormRow align='center'>
                  {isWidgetPagesLoading ? (
                    <Loading />
                  ) : (
                    <FormSelector
                      register={register('widgetPageId', registerNumberOption)}
                      choices={widgetPageChoices}
                      error={!!errors.widgetPageId?.message}
                      noSelectable
                      noSelectName='未選択'
                      size={SelectorSize.MEDIUM}
                    />
                  )}
                  <TextButton link={widgetPageListPath(siteId)} newTab size={12}>
                    デザイン参照ページ管理
                  </TextButton>
                </FormRow>
                <FormError message={errors.widgetPageId?.message} />
                <FormCaption size={12}>
                  AIがCSSを自動生成する際に参照するページです。設置を想定するページを登録/選択してください。
                </FormCaption>
              </FormRow>
              {/* Design Settings */}
              <FormRow align='start'>
                <FormRow direction='vertical' gap={0} size={TextBoxSize.FIT}>
                  <FormLabel fixWidth={null}>レイアウト</FormLabel>
                  <FormSelector
                    register={register('layoutType', {
                      onChange: (e) => {
                        // 4以上の表示数のままバンドルへ切り替えたら、選べる上限（3）にそろえる
                        if (
                          e.target.value === WidgetLayoutType.BUNDLE &&
                          !(maxItems >= BUNDLE_MAX_ITEMS_MIN && maxItems <= BUNDLE_MAX_ITEMS_MAX)
                        ) {
                          setValue('maxItems', BUNDLE_MAX_ITEMS_MAX)
                        }
                      }
                    })}
                    choices={Object.values(WidgetLayoutType).map((layout) => ({
                      name: widgetLayoutTypeName(layout),
                      value: layout
                    }))}
                  />
                </FormRow>
                <FormRow direction='vertical' gap={0}>
                  <FormLabel>表示数</FormLabel>
                  {layoutType === WidgetLayoutType.BUNDLE ? (
                    // バンドルは主商品に加えて並べる併売候補の数（1〜3）をボタンで選ぶ
                    <GroupButton
                      choices={BUNDLE_MAX_ITEMS_CHOICES}
                      value={String(maxItems)}
                      onSelect={(v) => setValue('maxItems', Number(v), { shouldValidate: true })}
                    />
                  ) : (
                    <FormSelector
                      register={register('maxItems', registerNumberOption)}
                      choices={[1, 2, 3, 4, 5, 6, 8, 10, 12, 16, 20].map((val) => ({
                        name: val.toString(),
                        value: val
                      }))}
                    />
                  )}
                  <FormError message={errors.maxItems?.message} />
                </FormRow>
              </FormRow>
              {/* Suggested prompts */}
              <FormRow direction='vertical' gap={8} size={TextBoxSize.FULL}>
                <FormRow direction='vertical' gap={0} size={TextBoxSize.FULL}>
                  <FormLabel>AI</FormLabel>
                  <div>
                    {suggestedPrompts.map((prompt, index) => (
                      <Badge
                        key={index}
                        badgeType={BadgeType.SQUARE_AI}
                        size={12}
                        onClick={() =>
                          setInstructions((prev) => (prev ? prev + '\n' + prompt : prompt))
                        }
                        preWrap
                      >
                        {prompt}
                      </Badge>
                    ))}
                    <IconButton
                      type={IconType.REPEAT}
                      text={'更新する'}
                      color={TextColor.LINK}
                      iconColor={IconColor.LINK}
                      size={12}
                      onClick={() => setSuggestedPrompts(pickRandomPrompts(ALL_PROMPTS, 4))}
                    />
                  </div>
                </FormRow>
                <TextArea
                  value={instructions}
                  onChange={(e) => setInstructions(e.target.value)}
                  placeholder='例: 背景を白にして角丸を大きくしてください'
                />
                <AiButton
                  size={'full'}
                  onClick={handleGenerateCSS}
                  disabled={!instructions.trim()}
                  loading={generating}
                >
                  AIでCSSを生成する
                </AiButton>
                <FormError message={genError} />
              </FormRow>
            </AreaContent>
          </div>
        </div>
      </ModalContent>
      <ModalFooter>
        <Button color='secondary' size='large' onClick={() => hide()}>
          閉じる
        </Button>
        <Button color={ButtonColor.PRIMARY} size='large' type='submit'>
          設定を反映する
        </Button>
      </ModalFooter>
    </form>
  )
}

export default VisualCssForm
